import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);
const SESSION_IDLE_TIMEOUT_MS = 60 * 60 * 1000;
const SESSION_ACTIVITY_SYNC_MS = 5 * 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check if user is logged in on mount using server session
    checkAuth();
  }, []);

  // Listen for 401 responses from any API call and clear auth state immediately
  useEffect(() => {
    const handleUnauthorized = () => {
      window.__sessionId = null;
      setUser(null);
      setIsAuthenticated(false);
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let timeoutId;
    let lastActivitySyncAt = Date.now();
    const expireSession = async () => {
      try {
        await api.logout();
      } catch (error) {
        console.error('Session timeout logout error:', error);
      }
      window.__sessionId = null;
      setUser(null);
      setIsAuthenticated(false);
    };
    const resetTimeout = () => {
      const now = Date.now();
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(expireSession, SESSION_IDLE_TIMEOUT_MS);
      if (now - lastActivitySyncAt >= SESSION_ACTIVITY_SYNC_MS) {
        lastActivitySyncAt = now;
        api.getCurrentUser().catch(() => {});
      }
    };
    const activityEvents = ['pointerdown', 'keydown', 'scroll', 'touchstart'];

    activityEvents.forEach((eventName) => window.addEventListener(eventName, resetTimeout));
    resetTimeout();

    return () => {
      window.clearTimeout(timeoutId);
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, resetTimeout));
    };
  }, [isAuthenticated]);

  const checkAuth = async () => {
    try {
      // Check authentication with server session
      const currentUser = await api.getCurrentUser();
      setUser(currentUser);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
    }
    setLoading(false);
  };

  const login = async (username, password) => {
    try {
      const response = await api.login(username, password);

      // Store sessionId in memory for cross-domain cookie fallback
      if (response.sessionId) {
        window.__sessionId = response.sessionId;
      }

      setUser(response.user);
      setIsAuthenticated(true);

      return response;
    } catch (error) {
      throw error;
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await api.changePassword(currentPassword, newPassword);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear cross-domain session reference
    window.__sessionId = null;
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const setAuthData = (user) => {
    setUser(user);
    setIsAuthenticated(true);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    updateUser,
    changePassword,
    checkAuth,
    setAuthData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;